import { Button, Group, Indicator, MultiSelect, Pill, Popover, Stack } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconFilter } from '@tabler/icons-react'
import { dateFormat, type FilterValue, type ListParams, labels, type ResolvedField, type Resource, today } from '@tnn/core'
import { RemoteMultiSelect, toneVars } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { useOptionsLoader } from '../api/resources'
import classes from './filters.module.css'

/**
 * The filter button of a list and of a report: each field that may be filtered on, as its type is chosen (a date range,
 * the options and the records of a reference picked from a list, any number of them), counted on the button while set
 */
export function FilterMenu({
  resource,
  fields: only,
  exclude,
  countExcept,
  params,
  setParams,
  t,
  locale,
}: {
  resource: Resource
  /** Only these, in this order (a report's); default every field with `filter` */
  fields?: readonly string[]
  exclude?: string
  /** Shown by its own control (the day picker), so not counted on the button */
  countExcept?: string
  params: ListParams
  setParams: (p: Partial<ListParams>) => void
  t: TFunction
  locale: 'th' | 'en'
}) {
  const fields = (only ? only.map((name) => resource.field(name)) : resource.fieldList).filter(
    (f): f is ResolvedField => !!f?.filter && f.name !== exclude,
  )
  if (fields.length === 0) return null
  const active = fields.filter((f) => f.name !== countExcept && params.filters[f.name]).length
  const setFilter = (name: string, value: FilterValue | undefined) => {
    const filters = { ...params.filters }
    if (value) filters[name] = value
    else delete filters[name]
    setParams({ filters })
  }
  return (
    // Never taller than the room under the button: a report's many filters scroll inside it
    <Popover position="bottom-start" width={340} trapFocus middlewares={{ flip: true, shift: { padding: 16 }, size: { padding: 16 } }}>
      <Popover.Target>
        <Indicator label={active} size={16} disabled={!active} offset={4}>
          <Button variant="default" size="sm" radius="sm" leftSection={<IconFilter size={16} stroke={1.75} />}>
            {t('actions.filter')}
          </Button>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown style={{ overflowY: 'auto', overscrollBehavior: 'contain' }}>
        <Stack gap="md">
          {fields.map((f) => (
            <FilterField
              key={f.name}
              resource={resource}
              field={f}
              value={params.filters[f.name]}
              onChange={(v) => setFilter(f.name, v)}
              t={t}
              locale={locale}
            />
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

function FilterField({
  resource,
  field,
  value,
  onChange,
  t,
  locale,
}: {
  resource: Resource
  field: ResolvedField
  value: FilterValue | undefined
  onChange: (v: FilterValue | undefined) => void
  t: TFunction
  locale: 'th' | 'en'
}) {
  const label = t(field.label)
  const values = value?.op === 'in' ? value.values : []
  if (field.type === 'date' || field.type === 'datetime') {
    // `today` / `now` are URL tokens the API resolves; show them as today's date
    const day = (v?: string) => (v === 'today' || v === 'now' ? today() : (v ?? null))
    const range = value?.op === 'range' ? [day(value.from), day(value.to)] : [null, null]
    return (
      <DatePickerInput
        type="range"
        allowSingleDateInRange
        label={label}
        clearable
        valueFormat={dateFormat(locale, 'short')}
        // In the filter popover: a calendar portalled out of it is "outside" it, so picking the first day closed the filter;
        // fixed, so the filter's scrolling doesn't clip it
        popoverProps={{ withinPortal: false, floatingStrategy: 'fixed' }}
        value={range as [string | null, string | null]}
        onChange={([from, to]) => onChange(from || to ? { op: 'range', from: from ?? undefined, to: to ?? undefined } : undefined)}
      />
    )
  }
  if (field.ref) return <RefFilter field={field} label={label} values={values} onChange={onChange} />
  if (field.options) return <OptionsFilter resource={resource} field={field} label={label} values={values} onChange={onChange} t={t} />
  return null
}

/**
 * A field's options, any number of them, from a list: a field whose values have colours (`tones`, a status) shows
 * each in its own, as its pill in the list's rows, a dot in the list and the colour on what is picked
 */
function OptionsFilter({
  resource,
  field,
  label,
  values,
  onChange,
  t,
}: {
  resource: Resource
  field: ResolvedField
  label: string
  values: string[]
  onChange: (v: FilterValue | undefined) => void
  t: TFunction
}) {
  const tones = field.tones
  const data = (field.options ?? []).map((o) => ({ value: o, label: t(labels.option(resource, field.name, o)) }))
  return (
    <MultiSelect
      label={label}
      data={data}
      value={values}
      onChange={(v) => onChange(v.length ? { op: 'in', values: v } : undefined)}
      searchable={data.length > 6}
      clearable
      comboboxProps={{ withinPortal: false, floatingStrategy: 'fixed' }}
      renderOption={
        tones
          ? ({ option }) => (
              <Group gap={8} wrap="nowrap" style={toneVars(tones[option.value] ?? 'neutral')}>
                <span className={classes.dot} />
                {option.label}
              </Group>
            )
          : undefined
      }
      renderPill={({ option, value, onRemove, disabled }) => (
        <Pill
          withRemoveButton={!disabled}
          onRemove={onRemove}
          className={tones ? classes.tone : undefined}
          style={tones ? toneVars(tones[String(value)] ?? 'neutral') : undefined}
        >
          {tones && <span className={classes.dot} />}
          {option?.label ?? value}
        </Pill>
      )}
    />
  )
}

function RefFilter({
  field,
  label,
  values,
  onChange,
}: {
  field: ResolvedField
  label: string
  values: string[]
  onChange: (v: FilterValue | undefined) => void
}) {
  const loader = useOptionsLoader(field.ref?.resource ?? '')
  return (
    <RemoteMultiSelect
      label={label}
      value={values}
      onChange={(v) => onChange(v.length ? { op: 'in', values: v } : undefined)}
      load={loader.load}
      resolve={loader.resolve}
      clearable
      comboboxProps={{ withinPortal: false, floatingStrategy: 'fixed' }}
    />
  )
}
